/**
 * Module responsible to handle logic related to Rudderstack that needs to be executed after page loads
 * Hook page load event and initialize Rudderstack SDK to ready event from Rudderstack
 *
 */

import * as AnalyticsData from '~/analytics/analytics-data';
import { SHOPIFY_TEMPLATE_TYPES } from '~/constants/shopify';
import * as DataDogLogs from '~/lib/datadog/datadog';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import * as RudderstackReady from '~/lib/rudderstack/rudderstack-ready-api';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as CustomerUtils from '~/utils/customer';

/**
 * Call identify, page and page loaded related events
 * - Identify, page and page related events should be called only once per page load
 * - Identify, page and page related events should be called only if the user has performance consent
 */
const sendAnalyticsEventsFromPageLoad = () => {
  // Analytics events should be sent only once and with consent provided
  if (
    !window.ALLBIRDS.Rudderstack.isIdentifyAndPageCalled &&
    window.ALLBIRDS.OneTrust.hasPerformanceConsent()
  ) {
    DataDogLogs.info('[Allbirds pixel] - RudderStack Page Load - sending analytics events');
    const marketingQueryParams = RudderstackUtils.marketingQueryParams();
    // Mandatory call to identify user after page load to ensure default traits are sent
    RudderstackClient.identifyUser({
      additionalTraits: {
        fbclid: marketingQueryParams.fbclid,
        gclid: marketingQueryParams.gclid,
        msclkid: marketingQueryParams.msclkid,
      },
      forceIdentify: true,
    });

    // Starting on Rudderstack V3, the default page call is not sent automatically
    RudderstackClient.page(window.ALLBIRDS?.page?.template || '', '', {
      client_platform: 'liquid',
    });

    // Send collection viewed event if on collection page template
    if (window.ALLBIRDS?.page?.template == SHOPIFY_TEMPLATE_TYPES.COLLECTION) {
      // Delegate logic to collect the data
      AnalyticsData.triggerProductListViewed();

      // Delegate logic to collect the data and check if any filters are applied
      AnalyticsData.triggerProductListFiltered();
    } else if (window.ALLBIRDS?.page?.template == SHOPIFY_TEMPLATE_TYPES.PRODUCT) {
      AnalyticsData.triggerProductViewed();
    }

    // Update flag to avoid multiple page and identify events
    window.ALLBIRDS.Rudderstack.isIdentifyAndPageCalled = true;
  }
};

const onPageLoad = () => {
  // Hook page load event
  document.addEventListener('DOMContentLoaded', () => {
    DataDogLogs.info('[Allbirds pixel] - RudderStack Page Load - page load event');

    // Flag to avoid multiple page and identify event calls
    // Reset flag on each page load
    window.ALLBIRDS.Rudderstack.isIdentifyAndPageCalled = false;

    // Initialize customer status cookies
    CustomerUtils.identifyPageloadEmail();

    // Hook on Rudderstack ready callback,
    // - Is triggered after SDK load, anonymousId and user defaul traits are ready
    // - Is triggered after OneTrust consent change
    RudderstackReady.rudderanalyticsReadyCallback(() => {
      DataDogLogs.info('[Allbirds pixel] - RudderStack Page Load - rudderstack ready event', {
        isIdentifyAndPageCalled: window.ALLBIRDS.Rudderstack.isIdentifyAndPageCalled,
      });

      // If OneTrust is ready, send analytics events
      if (window.ALLBIRDS.OneTrust.isOneTrustActiveGroupsReady()) {
        sendAnalyticsEventsFromPageLoad();
      } else {
        // If OneTrust is not ready, push analytics events into a pool to be triggered once OneTrust is ready
        window.ALLBIRDS.OneTrust.onConsentChange(() => {
          sendAnalyticsEventsFromPageLoad();
        });
      }
    }, RudderstackReady.CALLBACK_NAMES.RUDDERSTACK_PAGE_LOAD);
  });
};

export { onPageLoad };
